import { watchOnce } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import type { IBetBuilderCombination, IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
import { defineContext } from '@/libraries/utils/defineContext';

export const BetBuilderCombinationsContext = defineContext('BetBuilderCombinations', (betBuilderRecommendations: Ref<IBetBuilderRecommendation[]>) => {
    const combinations = ref<IBetBuilderRecommendation[]>([...betBuilderRecommendations.value.map(c => ({
        ...c,
        isLoaded: c.isLoaded ?? false,
        isValid: c.isValid ?? false,
    }))]);

    const combinationWithEventByKey = ref<Record<string, IBetBuilderCombination>>({});

    watch(betBuilderRecommendations, () => {
        const activeKeys = new Set(betBuilderRecommendations.value.map(c => c.combinationKey));
        combinations.value = betBuilderRecommendations.value.map(c => ({
            ...c,
            isLoaded: combinations.value.find(p => p.combinationKey === c.combinationKey)?.isLoaded ?? c.isLoaded ?? false,
            isValid: combinations.value.find(p => p.combinationKey === c.combinationKey)?.isValid ?? c.isValid ?? false,
        }));
        Object.keys(combinationWithEventByKey).forEach((key) => {
            if (!activeKeys.has(key)) {
                delete combinationWithEventByKey.value[key];
            }
        });
    });

    const removeBetBuilderCombination = (combinationKey: string) => {
        combinations.value = combinations.value.filter(c => c.combinationKey !== combinationKey);
        delete combinationWithEventByKey.value[combinationKey];
    };

    const updateBetBuilderCombinationLoaded = (combinationKey: string) => {
        const row = combinations.value.find(c => c.combinationKey === combinationKey);
        if (!row) return;
        row.isLoaded = true;
    };

    const setBetBuilderCombinationValid = (combinationKey: string, isValid: boolean) => {
        const row = combinations.value.find(c => c.combinationKey === combinationKey);
        if (!row) return;
        row.isValid = isValid;
    };

    const setCombinationWithEvent = (combinationKey: string, payload: IBetBuilderCombination) => {
        combinationWithEventByKey.value[combinationKey] = payload;
    };

    const visibleCombinations = computed(() => combinations.value.filter(c => c.isValid));

    const isAllLoaded = computed(() => combinations.value.length > 0 && combinations.value.every(c => c.isLoaded));

    const isFirstTimeAllLoaded = ref(false);
    watchOnce(isAllLoaded, () => {
        isFirstTimeAllLoaded.value = true;
    });

    const isExpanded = ref(false);

    function toggleExpand() {
        isExpanded.value = !isExpanded.value;
    }

    return {
        combinations,
        combinationWithEventByKey,
        visibleCombinations,
        removeBetBuilderCombination,
        updateBetBuilderCombinationLoaded,
        setBetBuilderCombinationValid,
        setCombinationWithEvent,
        isFirstTimeAllLoaded,
        isExpanded,
        toggleExpand,
    };
});
